import { useTu } from "@src/appV2/i18n/useTu";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputAdornment, Stack, TextField } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { type UseModalState } from "@src/lib/ui-react";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { DialogFooter } from "../../components/DialogFooter";
import { ResponsiveDialog } from "../../components/ResponsiveDialog";
import { Text } from "../../components/Text";
import { useCreateWorkplaceChargeLeadTimeDifferential } from "../api/useCreateWorkplaceChargeLeadTimeDifferential";

import {
  MAX_DIFFERENTIAL_PERCENT,
  MAX_THRESHOLD_HOURS,
  MIN_DIFFERENTIAL_PERCENT,
  MIN_THRESHOLD_HOURS,
} from "../constants";

import {
  type AddDifferentialFormInput,
  type AddDifferentialFormOutput,
  type ExistingDifferential,
  getDefaultFormValues,
  makeAddDifferentialFormSchema,
} from "./addLeadTimeDifferentialSchema";

interface AddLeadTimeDifferentialDialogProps {
  workplaceId: string;
  modalState: UseModalState;
  existingDifferentials: ExistingDifferential[];
}

export function AddLeadTimeDifferentialDialog(props: AddLeadTimeDifferentialDialogProps) {
  const { tu } = useTu("workplaceChargeLeadTimeDifferentials");

  const { workplaceId, modalState, existingDifferentials } = props;
  const { showSuccessToast, showErrorToast } = useToast();

  const { mutateAsync: createDifferential, isLoading } =
    useCreateWorkplaceChargeLeadTimeDifferential();

  const resolver = useMemo(
    () => zodResolver(makeAddDifferentialFormSchema(existingDifferentials, tu)),
    [existingDifferentials, tu]
  );

  const defaultFormValues = useMemo(
    () => getDefaultFormValues(existingDifferentials),
    [existingDifferentials]
  );

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isValid, isSubmitting },
  } = useForm<AddDifferentialFormInput, unknown, AddDifferentialFormOutput>({
    resolver,
    defaultValues: defaultFormValues,
    mode: "onChange",
  });

  const watchedThreshold = useWatch({ control, name: "thresholdInHours" });
  const watchedDifferential = useWatch({ control, name: "differentialPercent" });

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      return;
    }
    if (String(watchedThreshold) === "" && String(watchedDifferential) === "") {
      return;
    }
    void trigger();
  }, [
    watchedThreshold,
    watchedDifferential,
    existingDifferentials,
    trigger,
    modalState.modalIsOpen,
  ]);

  useEffect(() => {
    if (modalState.modalIsOpen) {
      reset(defaultFormValues);
    }
  }, [modalState.modalIsOpen, reset, defaultFormValues]);

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  const onSubmit = useCallback(
    async (values: AddDifferentialFormOutput) => {
      try {
        await createDifferential({
          workplaceId,
          thresholdInHours: values.thresholdInHours,
          incrementalMultiplier: values.differentialPercent / 100,
        });
        showSuccessToast(tu("Tier added"));
        handleClose();
      } catch (error) {
        let errorMessage = tu("Failed to add tier");
        if (isAxiosError<{ message?: string }>(error) && error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        showErrorToast(errorMessage);
      }
    },
    [createDifferential, handleClose, showErrorToast, showSuccessToast, workplaceId, tu]
  );

  return (
    <ResponsiveDialog
      showCloseButton
      modalState={modalState}
      title={tu("Add tier")}
      width={420}
      footerContent={
        <DialogFooter
          primaryAction={{
            label: tu("Create"),
            onClick: handleSubmit(onSubmit),
            isLoading: isLoading || isSubmitting,
            disabled: !isValid,
          }}
          secondaryAction={{
            label: tu("Cancel"),
            disabled: isLoading || isSubmitting,
            onClick: handleClose,
          }}
        />
      }
    >
      <Stack spacing={4}>
        <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="body2">
          {tu(
            "Shifts created within the threshold of start time will have the charge differential added on top of the Workplace Base Charge."
          )}
        </Text>

        <Stack spacing={1}>
          <Text semibold variant="body2">
            {tu("Threshold (hours)")}
          </Text>
          <Controller
            control={control}
            name="thresholdInHours"
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                required
                disabled={isLoading || isSubmitting}
                error={Boolean(errors.thresholdInHours)}
                helperText={
                  errors.thresholdInHours?.message ??
                  tu("Between {{minimumHours}} and {{maximumHours}} hours.", {
                    values: {
                      minimumHours: MIN_THRESHOLD_HOURS,
                      maximumHours: MAX_THRESHOLD_HOURS,
                    },
                  })
                }
                inputProps={{
                  inputMode: "numeric",
                  min: MIN_THRESHOLD_HOURS,
                  max: MAX_THRESHOLD_HOURS,
                  step: 1,
                }}
                placeholder={tu("e.g. 12")}
                size="small"
                type="number"
              />
            )}
          />
        </Stack>

        <Stack spacing={1}>
          <Text semibold variant="body2">
            {tu("Charge differential on top of base charge")}
          </Text>
          <Controller
            control={control}
            name="differentialPercent"
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                required
                disabled={isLoading || isSubmitting}
                error={Boolean(errors.differentialPercent)}
                helperText={
                  errors.differentialPercent?.message ??
                  tu("Between {{minimumPercent}}% and {{maximumPercent}}%.", {
                    values: {
                      minimumPercent: MIN_DIFFERENTIAL_PERCENT,
                      maximumPercent: MAX_DIFFERENTIAL_PERCENT,
                    },
                  })
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                  inputMode: "numeric",
                  min: MIN_DIFFERENTIAL_PERCENT,
                  max: MAX_DIFFERENTIAL_PERCENT,
                  step: 1,
                }}
                placeholder={tu("e.g. 5")}
                size="small"
                type="number"
              />
            )}
          />
        </Stack>
      </Stack>
    </ResponsiveDialog>
  );
}
